import React, { useState } from "react";
import { Card as RawCard } from "@/components/ui/card";
import { Button as RawButton } from "@/components/ui/button";
import { Label as RawLabel } from "@/components/ui/lable";
import { Switch as RawSwitch } from "@/components/ui/switch";
import { Bell, Mail, Gift, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

/**
 * @param {{
 *   user?: Record<string, any>,
 *   onUpdate: (data: any) => Promise<any>
 * }} props
 */
export default function NotificationSection({ user, onUpdate }) {
  const preferences = user?.notification_preferences || {};


  const [emailConfirmations, setEmailConfirmations] = useState(preferences.email_confirmations !== false);
  const [emailReminders, setEmailReminders] = useState(preferences.email_reminders !== false);
  const [promotionalEmails, setPromotionalEmails] = useState(preferences.promotional_emails || false);
  const [isSaving, setIsSaving] = useState(false);

  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Switch = /** @type {React.ComponentType<any>} */ (RawSwitch);
  const Label = /** @type {React.ComponentType<any>} */ (RawLabel);
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate({
        notification_preferences: {
          email_confirmations: emailConfirmations,
          email_reminders: emailReminders,
          promotional_emails: promotionalEmails
        }
      });
      alert("Notification preferences updated successfully!");
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update preferences. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}>
      
      <Card className="p-6 bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <Bell className="h-6 w-6 text-emerald-600" />
          <h2 className="text-2xl font-bold text-[#0B2421]">Notification Preferences</h2>
        </div>

        <p className="mb-6 text-gray-500">Choose how you want to receive updates and communications from TEEZY.

        </p>
        
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-4 p-4 bg-transparent border border-input rounded-lg">
            <div className="flex items-start gap-3 flex-1">
              <Mail className="h-5 w-5 text-emerald-600 mt-1" />
              <div>
                <Label htmlFor="confirmations" className="text-base font-semibold text-[#0B2421] cursor-pointer">
                  Booking Confirmations
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Receive email confirmations when you complete a booking
                </p>
              </div>
            </div>
            <Switch
              id="confirmations"
              checked={emailConfirmations}
              onCheckedChange={setEmailConfirmations} />
            
          </div>

          <div className="flex items-start justify-between gap-4 p-4 bg-transparent border border-input rounded-lg">
            <div className="flex items-start gap-3 flex-1">
              <Bell className="h-5 w-5 text-emerald-600 mt-1" />
              <div>
                <Label htmlFor="reminders" className="text-base font-semibold text-[#0B2421] cursor-pointer">
                  Booking Reminders
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Get reminder emails before your scheduled bookings
                </p>
              </div>
            </div>
            <Switch
              id="reminders"
              checked={emailReminders}
              onCheckedChange={setEmailReminders} />
            
          </div>

          <div className="flex items-start justify-between gap-4 p-4 bg-transparent border border-input rounded-lg">
            <div className="flex items-start gap-3 flex-1">
              <Gift className="h-5 w-5 text-emerald-600 mt-1" />
              <div>
                <Label htmlFor="promotional" className="text-base font-semibold text-[#0B2421] cursor-pointer">
                  Promotional Emails
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Receive special offers, deals, and updates about new services
                </p>
              </div>
            </div>
            <Switch
              id="promotional"
              checked={promotionalEmails}
              onCheckedChange={setPromotionalEmails} />
            
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto hover:bg-[#0B2421]/85 h-12 text-white bg-[#0B2421]">
              
              {isSaving ?
              <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </> :

              "Save Preferences"
              }
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>);

}